import * as React from "react";
import styled from "@emotion/styled";
import { BlockTag, Stack, Text } from "atoms";
import { GenericImage, type Image } from "features/images";
import { uniqueId } from "lodash";

type WizardStepOptionProps = {
  description?: string | React.ReactNode;
  groupName?: string;
  image?: Image | string;
  isComingSoon?: boolean;
  label: string;
  type: "radio" | "checkbox" | "multiselect" | "button" | "disabled";
  onClick?: () => void;
};

function WizardStepOption({
  label,
  groupName,
  image,
  isComingSoon,
  description,
  type,
  onClick,
}: WizardStepOptionProps) {
  const [id] = React.useState(uniqueId());
  const optionContent = (
    <OptionMain>
      {image && <GenericImage src={image} />}
      <OptionBody>
        <Stack gap="0">
          <Title>{label}</Title>
          {description && <Description>{description}</Description>}
          {isComingSoon && (
            <BlockTag bg="accent1LL" color="fg">
              Coming Soon
            </BlockTag>
          )}
        </Stack>
      </OptionBody>
    </OptionMain>
  );
  if (isComingSoon || type === "disabled") {
    return <OptionDisabled as="div">{optionContent}</OptionDisabled>;
  }
  return type === "button" ? (
    <Option isButton as="button" onClick={onClick}>
      {optionContent}
    </Option>
  ) : (
    <>
      <OptionInput
        hidden
        type={type === "radio" ? "radio" : "checkbox"}
        name={groupName}
        id={id}
      />
      <Option htmlFor={id}>{optionContent}</Option>
    </>
  );
}

const OptionInput = styled.input({});

const Option = styled.label<{ isButton?: boolean }>(({ isButton, theme }) => ({
  ...(isButton && {
    appearance: "none",
  }),
  backgroundColor: theme.colors.bg,
  borderRadius: theme.borderRadius.sm,
  border: 0,
  padding: 0,
  boxShadow: theme.boxShadow.light,
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  transition: ".2s ease",
  transitionProperty: "box-shadow, transform",
  "&,*": {
    textAlign: "center",
  },
  "&:hover, &:focus-visible": {
    boxShadow: theme.boxShadow.dark,
    transform: "translateY(-2px)",
  },
  "input:checked + &": {
    borderColor: theme.colors.fg,
    outline: "4px solid",
    outlineColor: theme.colors.fg,
  },
}));

const OptionMain = styled.div({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: 0,
});
const OptionDisabled = styled(Option)({
  pointerEvents: "none",
  opacity: 0.5,
}).withComponent("div");

const Title = styled(Text)(({ theme }) => ({
  ...theme.fonts.display,
  color: theme.colors.fg,
  fontSize: theme.fontSizes.sm,
  lineHeight: 1.2,
}));
const Description = styled(Text)(({ theme }) => ({
  color: theme.colors.fg70,
  fontSize: 13,
}));
const OptionBody = styled.div({
  padding: 10,
  display: "flex",
  flexDirection: "column",
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
});

export { WizardStepOption };
